import { useState, useEffect, useCallback } from 'react';
import { fetchListItems, addListItem, updateListItem, deleteListItem, reorderList } from '@/services/cms';
import { useCms } from '@/context/CmsContext';

export function useCmsList<T extends { id?: number }>(slug: string, limit = 100) {
    const { publishVersion } = useCms();
    const [items, setItems] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        // Fetch all pages
        async function fetchAll() {
            const all: T[] = [];
            let page = 0;
            while (true) {
                const batch = await fetchListItems<T>(slug, page, limit);
                all.push(...batch);
                if (batch.length < limit) break;
                page++;
            }
            if (!cancelled) {
                setItems(all);
                setLoading(false);
            }
        }

        fetchAll().catch(() => {
            if (!cancelled) setLoading(false);
        });

        return () => { cancelled = true; };
    }, [slug, limit, refreshKey, publishVersion]);

    const refetch = () => setRefreshKey((k) => k + 1);

    const add = useCallback(async (data: Omit<T, 'id'>, sortOrder?: number) => {
        const id = await addListItem(slug, data as any, sortOrder);
        refetch();
        return id;
    }, [slug]);

    const update = useCallback(async (id: number, data: Partial<T>, sortOrder?: number) => {
        await updateListItem(slug, id, data as any, sortOrder);
        refetch();
    }, [slug]);

    const remove = useCallback(async (id: number) => {
        await deleteListItem(slug, id);
        refetch();
    }, [slug]);

    const reorder = useCallback(async (ids: number[]) => {
        await reorderList(slug, ids);
        refetch();
    }, [slug]);

    return { items, loading, refetch, add, update, remove, reorder };
}
